import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { getApiUrl } from '../../lib/api';
import { countryFlag } from '../../lib/country-utils';

interface CountryLaw {
  country: string;
  country_code: string;
  status: string;
  details?: string;
}

type Answer = 'Legal' | 'Decriminalized' | 'Medical Only' | 'Illegal';

const ANSWER_OPTIONS: Answer[] = ['Legal', 'Decriminalized', 'Medical Only', 'Illegal'];

function normalizeStatus(status: string | null | undefined): Answer {
  if (typeof status !== 'string') return 'Illegal';
  const s = status.toLowerCase();
  if (s === 'legal' || s.includes('legal purchase') || s.includes('fully legal')) return 'Legal';
  if (s.includes('decriminalized') || s.includes('tolerated')) return 'Decriminalized';
  if (s.includes('medical')) return 'Medical Only';
  return 'Illegal';
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const ANSWER_COLORS: Record<Answer, string> = {
  Legal: 'border-green-500/40 bg-green-500/10 text-green-400',
  Decriminalized: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-400',
  'Medical Only': 'border-blue-500/40 bg-blue-500/10 text-blue-400',
  Illegal: 'border-red-500/40 bg-red-500/10 text-red-400',
};

type Phase = 'loading' | 'ready' | 'playing' | 'result';

const ROUNDS = 10;

export default function LegalOrNotTab() {
  const [allCountries, setAllCountries] = useState<CountryLaw[]>([]);
  const [gameCountries, setGameCountries] = useState<CountryLaw[]>([]);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [answered, setAnswered] = useState<Answer | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');

  useEffect(() => {
    fetch(getApiUrl('/api/country-laws'))
      .then(r => r.json())
      .then(data => {
        const items: CountryLaw[] = Array.isArray(data) ? data : data.data ?? [];
        setAllCountries(items.filter(c => c.status && c.country_code));
        setPhase('ready');
      })
      .catch(() => setPhase('ready'));
  }, []);

  const startGame = useCallback(() => {
    const picked = shuffle(allCountries).slice(0, ROUNDS);
    setGameCountries(picked);
    setRound(0);
    setScore(0);
    setStreak(0);
    setBestStreak(0);
    setAnswered(null);
    setPhase('playing');
  }, [allCountries]);

  const current = gameCountries[round];
  const correctAnswer = current ? normalizeStatus(current.status) : 'Illegal';

  const handleAnswer = (ans: Answer) => {
    if (answered) return;
    setAnswered(ans);
    if (ans === correctAnswer) {
      setScore(s => s + 1);
      setStreak(s => {
        const ns = s + 1;
        setBestStreak(b => Math.max(b, ns));
        return ns;
      });
    } else {
      setStreak(0);
    }
  };

  const nextRound = () => {
    if (round + 1 >= gameCountries.length) {
      setPhase('result');
    } else {
      setRound(r => r + 1);
      setAnswered(null);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-6">
      {phase === 'loading' && (
        <div className="text-center py-16 text-white/40">Loading country data...</div>
      )}

      {phase === 'ready' && (
        <div className="text-center py-12 space-y-4">
          <h3 className="text-white text-xl font-bold">Legal or Not?</h3>
          <p className="text-white/50 text-sm">
            Guess the cannabis legal status in {ROUNDS} countries.
          </p>
          <p className="text-white/30 text-xs">{allCountries.length} countries loaded</p>
          <button
            onClick={startGame}
            disabled={allCountries.length < ROUNDS}
            className="px-6 py-3 bg-[#4ade80]/20 border border-[#4ade80]/40 text-[#4ade80] font-semibold rounded-xl hover:bg-[#4ade80]/30 transition disabled:opacity-30"
          >
            Start Game
          </button>
        </div>
      )}

      {phase === 'playing' && current && (
        <motion.div
          key={round}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-5"
        >
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-white/40 text-xs">Round {round + 1}/{ROUNDS}</span>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-[#4ade80] font-semibold">Score: {score}</span>
              {streak > 1 && (
                <span className="text-[#d4a574] font-semibold">{streak}x streak</span>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#4ade80]/60 rounded-full transition-all duration-300"
              style={{ width: `${((round + 1) / ROUNDS) * 100}%` }}
            />
          </div>

          {/* Country card */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 text-center space-y-3">
            <div className="text-6xl">{countryFlag(current.country_code)}</div>
            <h3 className="text-white text-2xl font-bold">{current.country}</h3>
            <p className="text-white/40 text-sm">What is the cannabis legal status?</p>
          </div>

          {/* Answer buttons */}
          <div className="grid grid-cols-2 gap-3">
            {ANSWER_OPTIONS.map(opt => {
              let cls = 'border-white/10 bg-white/[0.02] text-white/60 hover:border-white/20';
              if (answered) {
                if (opt === correctAnswer) cls = ANSWER_COLORS[opt];
                else if (opt === answered && opt !== correctAnswer) cls = 'border-red-500/40 bg-red-500/10 text-red-400 line-through';
                else cls = 'border-white/5 bg-white/[0.01] text-white/20';
              }
              return (
                <button
                  key={opt}
                  onClick={() => handleAnswer(opt)}
                  disabled={answered !== null}
                  className={`px-4 py-3 rounded-xl border text-sm font-semibold transition ${cls}`}
                >
                  {opt}
                </button>
              );
            })}
          </div>

          {/* Feedback */}
          {answered && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`text-sm font-semibold ${answered === correctAnswer ? 'text-[#4ade80]' : 'text-red-400'}`}>
                  {answered === correctAnswer ? 'Correct!' : `Wrong! It's ${correctAnswer}`}
                </span>
                <button
                  onClick={nextRound}
                  className="px-4 py-2 bg-white/5 border border-white/10 text-white/70 rounded-xl text-sm hover:bg-white/10 transition"
                >
                  {round + 1 >= ROUNDS ? 'See Results' : 'Next Country'}
                </button>
              </div>
              {current.details && (
                <p className="text-white/40 text-xs leading-relaxed">{current.details}</p>
              )}
            </div>
          )}
        </motion.div>
      )}

      {phase === 'result' && (
        <div className="text-center py-12 space-y-5">
          <div className="text-5xl font-bold text-[#4ade80]">{score}/{ROUNDS}</div>
          <p className="text-white/60 text-sm">
            {score >= 8 ? 'Legal expert!' : score >= 5 ? 'Decent knowledge!' : 'The world is complex!'}
          </p>
          {bestStreak > 1 && (
            <p className="text-[#d4a574] text-xs">Best streak: {bestStreak}x</p>
          )}
          <button
            onClick={startGame}
            className="px-5 py-2.5 bg-[#4ade80]/20 border border-[#4ade80]/40 text-[#4ade80] font-semibold rounded-xl hover:bg-[#4ade80]/30 transition text-sm"
          >
            Play Again
          </button>
        </div>
      )}
    </div>
  );
}
